import { useState, type FormEvent } from 'react';
import type { Priority, Task } from '@nepal-football/shared';

import { useCreateTask, useUpdateTask } from '../api/hooks';
import { ApiClientError } from '../api/client';

export interface TaskFormProps {
  /** When provided, the form opens in edit mode. */
  task?: Task;
  onSave: (t: Task) => void;
  onCancel: () => void;
}

const PRIORITIES: Priority[] = ['high', 'medium', 'low'];

export default function TaskForm({ task, onSave, onCancel }: TaskFormProps) {
  const isEdit = task !== undefined;
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [name,        setName]        = useState(task?.name        ?? '');
  const [description, setDescription] = useState(task?.task        ?? '');
  const [time,        setTime]        = useState(task?.time        ?? '');
  const [priority,    setPriority]    = useState<Priority>(task?.priority ?? 'medium');
  const [done,        setDone]        = useState(task?.done        ?? false);

  const [errors, setErrors] = useState<{
    name?: string;
    task?: string;
    time?: string;
    submit?: string;
  }>({});

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    const validation: typeof errors = {};
    if (name.trim().length === 0) validation.name = 'Team / role label is required';
    if (description.trim().length === 0) validation.task = 'Task description is required';
    if (time.trim().length === 0) validation.time = 'Time is required';
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});

    const payload = {
      name: name.trim(),
      task: description.trim(),
      time: time.trim(),
      priority,
      done,
    };

    try {
      const result = isEdit
        ? await updateMutation.mutateAsync({ id: task!.id, ...payload })
        : await createMutation.mutateAsync(payload);
      onSave(result);
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'Could not save task.';
      setErrors({ submit: message });
    }
  }

  return (
    <form className="task-form" onSubmit={handleSubmit} noValidate>
      <div className="modal-header">
        <span className="modal-title">
          {isEdit ? '✏️ Edit Task' : '➕ Add Task'}
        </span>
        <button
          type="button"
          className="modal-close"
          onClick={onCancel}
          aria-label="Close form"
        >
          ✕
        </button>
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="task-name">Team / role *</label>
        <input
          id="task-name"
          className="reg-input"
          type="text"
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Setup Crew"
          aria-invalid={!!errors.name}
          required
        />
        {errors.name && <div className="reg-error" role="alert">{errors.name}</div>}
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="task-desc">Task description *</label>
        <textarea
          id="task-desc"
          className="reg-input"
          rows={3}
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What needs to be done?"
          aria-invalid={!!errors.task}
          required
        />
        {errors.task && <div className="reg-error" role="alert">{errors.task}</div>}
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="task-time">Time *</label>
        <input
          id="task-time"
          className="reg-input"
          type="text"
          maxLength={100}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          placeholder='e.g. "9:00 AM" or "All Day"'
          aria-invalid={!!errors.time}
          required
        />
        {errors.time && <div className="reg-error" role="alert">{errors.time}</div>}
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="task-priority">Priority</label>
        <select
          id="task-priority"
          className="reg-input"
          value={priority}
          onChange={(e) => setPriority(e.target.value as Priority)}
        >
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
      </div>

      {isEdit && (
        <div className="reg-field">
          <label className="reg-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={done}
              onChange={(e) => setDone(e.target.checked)}
            />
            Mark as complete
          </label>
        </div>
      )}

      {errors.submit && (
        <div className="reg-error" role="alert" style={{ marginBottom: 12 }}>
          ⚠️ {errors.submit}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="reg-btn reg-btn-ghost"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="reg-btn"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add task'}
        </button>
      </div>
    </form>
  );
}
